/**
 * Deferred video loading for performance optimization.
 *
 * - Hero videos ([data-deferred-video]): Load after page load, only for the active viewport (mobile/desktop).
 * - Card videos ([data-deferred-card-video]): Load via IntersectionObserver when scrolled into view.
 */

const MOBILE_BREAKPOINT = 750;

/**
 * Activates a video element by swapping data-src to src on its sources.
 * @param {HTMLVideoElement} video
 * @param {boolean} [autoplay=true] - Whether to auto-play after loading
 */
function activateVideo(video, autoplay = true) {
  if (video.dataset.activated) return;
  video.dataset.activated = 'true';

  const sources = video.querySelectorAll('source[data-src]');
  for (const source of sources) {
    source.src = source.dataset.src;
    source.removeAttribute('data-src');
  }

  video.load();
  if (autoplay) {
    video.play().catch(() => {});
  }
}

/**
 * Hero video: load only the viewport-appropriate video after the page finishes loading.
 */
function initHeroVideos() {
  const heroVideos = document.querySelectorAll('[data-deferred-video]');
  if (!heroVideos.length) return;

  const isDesktop = window.innerWidth >= MOBILE_BREAKPOINT;
  const targetViewport = isDesktop ? 'desktop' : 'mobile';

  for (const video of heroVideos) {
    if (video.dataset.deferredVideo === targetViewport) {
      activateVideo(video);
    }
  }
}

/**
 * Card videos: load when they enter the viewport.
 */
function initCardVideos() {
  const cardVideos = document.querySelectorAll('[data-deferred-card-video]');
  if (!cardVideos.length) return;

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          activateVideo(/** @type {HTMLVideoElement} */ (entry.target), false);
          observer.unobserve(entry.target);
        }
      }
    },
    { rootMargin: '200px 0px' }
  );

  for (const video of cardVideos) {
    observer.observe(video);
  }
}

function init() {
  initHeroVideos();
  initCardVideos();
}

if (document.readyState === 'complete') {
  init();
} else {
  window.addEventListener('load', init, { once: true });
}
